import { KeyValueObj } from "@/common/types";
import { formatToNaira, formatKey } from "@/common/utils/general";
import { IAddedBatch } from "@/common/types/others";
import { ScannedItem } from "@/common/components/scan";

export interface ScannedItemData extends ScannedItem {
  batchNumber: { type: string; value: string };
  cartonSerial: { type: string; value: string };
  packSerial: { type: string; value: string };
  unitSerial: { type: string; value: string };
  status: { type: string; value: string };
  expiry_date: { type: string; value: string };
  manufacture_date: { type: string; value: string };
  strength: { type: string; value: string };
  strengthUnit: { type: string; value: string };
  brandName: { type: string; value: string };
  inventory_id: { type: string; value: string };
}

type LogisticCompanyPreview = {
  companyName: string;
  details: KeyValueObj[];
};

export const getProductDetailsForm = (category: string) => {
  const form = [
    {
      name: "name",
      prompt: "Product Name",
      placeholder: "Panadol",
      inputType: "text",
      fullWidth: false,
      shortWidth: false,
      rules: {
        minLength: {
          expectedValue: 2,
          errorMessage: "Product name should be atleast two characters",
        },
      },
    },
    {
      name: "description",
      prompt: "Description",
      placeholder: "Light green box of clean Penicilin bottles",
      isFullWidth: false,
      inputtype: "textarea",
    },
    {
      name: "value",
      prompt: "Estimated monetary value of product in naira",
      // inputType: "number",
      inputType: "text",
      placeholder: "30000",

      rules: {
        formatAsCurrency: {
          expectedValue: true,
        },
      },
    },
    {
      name: "weight",
      prompt: "Product Weight(kg)",
      inputType: "number",
      placeholder: "400",
      fullWidth: false,
      shortWidth: false,
      rules: {
        minLength: {
          expectedValue: 1,
          errorMessage: "Weight is required",
        },
      },
    },
    {
      name: "length",
      prompt: "Package Length(cm))",
      inputType: "number",
      placeholder: "20",
      fullWidth: false,
      shortWidth: false,
      rules: {
        minLength: {
          expectedValue: 1,
          errorMessage: "Length is required",
        },
      },
    },
    {
      name: "width",
      prompt: "Package Width(cm)",
      inputType: "number",
      placeholder: "400",
      fullWidth: false,
      shortWidth: false,
      rules: {
        minLength: {
          expectedValue: 1,
          errorMessage: "Width is required",
        },
      },
    },
    {
      name: "height",
      prompt: "Package Height(cm)",
      inputType: "number",
      placeholder: "400",
      fullWidth: false,
      shortWidth: false,
      rules: {
        minLength: {
          expectedValue: 1,
          errorMessage: "Height is required",
        },
      },
    },
    {
      name: "package_type",
      prompt: "Packaging Type",
      inputType: "selectInput",
      placeholder: "Carton",
      fullWidth: false,
      shortWidth: false,
      returnId: true,
      options: [
        {
          title: "Box",
          value: "box",
          IDBDatabase: "box",
        },
        {
          title: "Crate",
          value: "crate",
          id: "crate",
        },
        {
          title: "Insulated Box",
          value: "insulated_box",
          id: "insulated_box",
        },
      ],
    },
    {
      name: "quantity",
      prompt: "Quantity",
      placeholder: "50",
      inputType: "number",
      shortWidth: true,
      rules: {
        minLength: {
          expectedValue: 1,
          errorMessage: "Quantity is required",
        },
      },
    },
    {
      name: "category",
      prompt: "Category",
      inputType: "selectInput",
      placeholder: "Refrigerated",
      returnId: true,
      options: [
        { title: "Cold Storage", value: "cold_storage", id: "cold_storage" },
        {
          title: "Non Cold Storage",
          value: "non_cold_storage",
          id: "non_cold_storage",
        },
      ],
    },
  ];

  const temperatureForm = [
    {
      name: "min_temperature",
      prompt: "Minimum Temperature('C)",
      inputType: "number",
      placeholder: "18",
      rules: {
        minLength: {
          expectedValue: 1,
          errorMessage: "Minimum temperature is required",
        },
      },
    },
    {
      name: "max_temperature",
      prompt: "Maximum Temperatrue",
      placeholder: "23",
      inputType: "number",
      rules: {
        minLength: {
          expectedValue: 1,
          errorMessage: "Maximum temperature is required",
        },
      },
    },
  ];

  if (category === "cold_storage") return [...form, ...temperatureForm];
  return form;
};

export const addedProductHeaders = [
  "Name",
  "Description",
  "Estimated Value",
  "Category",
  "Weight",
  "Length",
  "Width",
  "Height",
  "Packaging Type",
  "Quantity",
];

export const addedProductKeys = [
  "name",
  "description",
  "value",
  "category",
  "weight",
  "width",
  "length",
  "height",
  "package_type",
  "quantity",
];

export const getAddedProductTableBody = (products: KeyValueObj[]) =>
  products?.map((product: KeyValueObj) => ({
    id: product?.id,
    name: {
      type: "string",
      value: product?.name,
    },
    description: {
      type: "string",
      value: product?.description,
    },
    value: {
      type: "string",
      value: formatToNaira(product?.value),
    },
    category: {
      type: "string",
      value: product?.category,
    },
    weight: {
      type: "string",
      value: product?.weight,
    },
    length: {
      type: "string",
      value: product?.length,
    },
    width: {
      type: "string",
      value: product?.length,
    },
    height: {
      type: "string",
      value: product?.height,
    },
    package_type: {
      type: "string",
      value: product?.package_type,
    },
    quantity: {
      type: "string",
      value: product?.quantity,
    },
  }));

export const getReceivingCompanyOptions = (
  companies: KeyValueObj[] = [],
): KeyValueObj[] =>
  companies.map((company) => ({
    title: company?.name ?? company?.title ?? "Unnamed",
    value: company?.id,
    id: company?.id,
  }));

export const getDeliveryCompanyOptions = (companies: KeyValueObj[]) =>
  companies?.map((company: KeyValueObj) => ({
    title: company?.name || "--",
    value: company?.id,
    id: company?.id,
  }));

export const getTotalWeight = (addedProducts: KeyValueObj[]) =>
  addedProducts?.reduce(
    (acc, curr) => acc + parseFloat(curr?.weight) * parseFloat(curr?.quantity),
    0,
  );

export const getTotalRange = (
  addedProducts: KeyValueObj[],
  logisticCompany: KeyValueObj,
) => {
  const totalWeight = getTotalWeight(addedProducts);
  const lowest = logisticCompany.lowest_price * totalWeight;
  const highest = logisticCompany.highest_price * totalWeight;
  if (isNaN(lowest) && isNaN(highest)) return "-- - --";
  return `₦${lowest} - ₦${highest}`;
};

export function getRecipentViewDetails(company: KeyValueObj) {
  return [
    {
      title: "Company Name",
      value: company?.name,
    },
    {
      title: "Company Phone Number",
      value: company?.phone_number,
    },
    {
      title: "Company Secondary Phone Number",
      value: company?.secondary_phone_number,
    },

    {
      title: "Company Email",
      value: company?.email,
    },
  ];
}
export function getRecipentPreviewDetails(
  company: KeyValueObj,
  address: KeyValueObj,
) {
  return [
    {
      title: "Company Name",
      value: company?.name,
    },
    {
      title: "Company RC Number",
      value: company?.rc_number,
    },
    {
      title: "Company Phone Number",
      value: company?.phone_number,
    },
    {
      title: "Company Email",
      value: company?.email,
    },
    {
      title: "Delivery Address",
      value: address?.address_line_one + ", " + address?.address_line_two,
    },
    {
      title: "Delivery City",
      value: address?.city,
    },
    {
      title: "Delivery LGA",
      value: address?.lga,
    },
    {
      title: "Delivery State",
      value: address?.state,
    },
  ];
}

export function getLogisticViewDetails(company: KeyValueObj) {
  return [
    {
      title: "Company Name",
      value: company?.name,
    },
    {
      title: "Company Phone Number",
      value: company?.phone_number,
    },
    {
      title: "Company Email",
      value: company?.email,
    },
    {
      title: "Address",
      value: `${company?.street_address || "--"}, ${company?.state || "--"}`,
    },
  ];
}
export function getLogisticPreviewDetailsItem(
  company: KeyValueObj,
  addedProducts: KeyValueObj[],
) {
  return [
    {
      title: "Company Name",
      value: company?.name,
    },
    {
      title: "Company Phone Number",
      value: company?.phone_number,
    },
    {
      title: "Company Email",
      value: company?.email,
    },
    {
      title: "Address",
      value: `${company.street_address || "--"}, ${company.state || "--"}`,
    },
    {
      title: "Price Range (kg)",
      value: `${company.lowest_price || "--"} - ${
        company.highest_price || "--"
      }`,
    },
    {
      title: "Total Delivery Weigth",
      value: getTotalWeight(addedProducts),
    },
    {
      title: "Total Cost Range",
      value: getTotalRange(addedProducts, company),
    },
  ];
}

export const getLogisticPreviewDetails = (
  deliveryItemsDetails: any,
): LogisticCompanyPreview[] => {
  const logisticCompanies = deliveryItemsDetails?.logistics_company || [];

  return logisticCompanies.map((company: any) => ({
    companyName: company?.name || "Unknown Company",
    details: [
      { title: "Phone Number", value: company?.phone_number || "" },
      { title: "Email", value: company?.email || "" },
      {
        title: "Address",
        value: `${company?.street_address || ""} ${company?.city || ""} ${company?.state || ""}`,
      },
    ],
  }));
};

export function getPickupPreviewDetails(
  pickupOption: string,
  senderAddressType: string,
  address: KeyValueObj,
) {
  if (pickupOption === "sender_drop_off")
    return [
      {
        title: "Pickup Type",
        value: "Logistic Pickup",
      },
    ];

  return [
    {
      title: "Pickup Type",
      value: formatKey(pickupOption),
    },
    {
      title: "Address Type",
      value: formatKey(senderAddressType),
    },
    {
      title: "Sender Address",
      value: address?.address_line_one + ", " + address?.address_line_two,
    },
    {
      title: "Sender City",
      value: address?.city,
    },
    {
      title: "Sender LGA",
      value: address?.lga,
    },
    {
      title: "Sender State",
      value: address?.state,
    },
  ];
}

export const getOtherDetails = (details: KeyValueObj) => [
  {
    title: "Preffered Vehicle",
    value: details?.vehicle_needed,
  },
  {
    title: "Expected Delivery Date",
    value: details?.expected_delivery_date,
  },
  {
    title: "Sender's Secondary Phone Number",
    value: details?.sender_phone_number,
  },
  {
    title: "Recipient's Secondary Phone Number",
    value: details?.recipent_phone_number,
  },
];

export const vehicleOptions = [
  {
    title: "Bus",
    value: "bus",
    id: "bus",
  },
  {
    title: "Truck",
    value: "truck",
    id: "truck",
  },
  {
    title: "Van",
    value: "van",
    id: "van",
  },
  {
    title: "Car",
    value: "car",
    id: "car",
  },
  {
    title: "Other",
    value: "other",
    id: "other",
  },
];

export const temperatureOption = [
  { title: "cold_storage", value: "cold_storage", id: "cold_storage" },
  {
    title: "non_cold_storage",
    value: "non_cold_storage",
    id: "non_cold_storage",
  },
];

export const packageType = [
  {
    title: "box",
    value: "box",
    IDBDatabase: "box",
  },
  {
    title: "crate",
    value: "crate",
    id: "crate",
  },
  {
    title: "insulated Box",
    value: "insulated_box",
    id: "insulated_box",
  },
];

export const inComingTableHeaders = [
  "Tracking Number",
  "Sending Company",
  "Logistic Company",
  "Request Status",
  "Delivery Status",
];

export const sentTableHeaders = [
  "Tracking Number",
  "Receiving Company",
  "Logistic Company",
  "Date Initiated",
  "Request Status",
  "Delivery Status",
];

export const incomingTableKeys = [
  "trackingID",
  "sendingCompany",
  "logisticCompany",
  "requestStatus",
  "deliveryStatus",
];
export const sentTableKeys = [
  "trackingID",
  "receivingCompany",
  "logisticCompany",
  "dateInitiated",
  "requestStatus",
  "deliveryStatus",
];

export function getIncomingTableData(data: KeyValueObj[]) {
  return data?.map((datum: KeyValueObj) => ({
    id: datum?.delivery_request?.id,
    trackingID: {
      type: "string",
      value: datum?.delivery?.tracking_number ?? "--",
    },
    sendingCompany: {
      type: "string",
      value: datum?.delivery_request?.sender?.name,
    },
    logisticCompany: {
      type: "string",
      value: datum?.delivery_request?.company?.name,
    },
    requestStatus: {
      type: "string",
      value: datum?.delivery_request?.request_status,
    },
    deliveryStatus: {
      type: "string",
      value: datum?.delivery?.delivery_status ?? "--",
    },
  }));
}
export function getSentTableData(data: KeyValueObj[]) {
  return data?.map((datum: KeyValueObj) => ({
    id: datum?.delivery_request?.id,
    trackingID: {
      type: "string",
      value: datum?.delivery?.tracking_number ?? "--",
    },
    receivingCompany: {
      type: "string",
      value: datum?.delivery_request?.recipient?.name,
    },
    logisticCompany: {
      type: "string",
      value: datum?.delivery_request?.company?.name,
    },
    dateInitiated: {
      type: "string",
      value: new Date(
        datum?.delivery_request?.sender?.created_at,
      ).toDateString(),
    },
    requestStatus: {
      type: "string",
      value: datum?.delivery_request?.request_status,
    },
    deliveryStatus: {
      type: "string",
      value: datum?.delivery?.delivery_status ?? "--",
    },
  }));
}

export function getSentDelivery(data: KeyValueObj) {
  return {
    delivery_items: data?.delivery_items,
    recipient: data?.recipient,
    company: data?.company,
    delivery: data?.delivery,
    ...data,
  };
}

export const DeliveryFilterOptions = [
  {
    name: "created_at",
    label: "Created Date",
    type: "date",
  },
  {
    name: "expected_date_of_ar",
    label: "Expected Delivery Date",
    type: "date",
  },

  {
    name: "request_status",
    label: "Request Status",
    type: "radio",
    options: [
      { label: "Accepted", value: "accepted" },
      { label: "Rejected", value: "rejected" },
      { label: "Assigned", value: "assigned" },
      { label: "Closed", value: "closed" },
      { label: "Rejected by Client", value: "rejected_by_client" },
      { label: "Accept by Client", value: " accepted_by_client" },
    ],
  },
];

export const handleScannedItem = (data: any) => {
  return data?.items?.map((recallData: any) => ({
    id: recallData?.id,
    inventory_id: {
      type: "string",
      value: data?.inventory?.id ?? recallData?.inventory_id ?? "",
    },
    batchNumber: { type: "string", value: data?.inventory?.batch_number },
    cartonSerial: { type: "string", value: data?.serial_number },
    packSerial: { type: "string", value: recallData?.packSerial || "NA" },
    unitSerial: { type: "string", value: recallData?.serial_number },
    status: { type: "string", value: recallData?.status },
    manufacture_date: {
      type: "string",
      value: data?.inventory?.manufacture_date,
    },
    expiry_date: { type: "string", value: data?.inventory?.expiry_date },
    strength: { type: "string", value: data?.product?.strength },
    strengthUnit: { type: "string", value: data?.product?.strength_unit },
    brandName: { type: "string", value: data?.product?.brand_name },
  }));
};

export const scanTableHeaders = [
  "Batch Number",
  "Carton Serial",
  "Pack Serial",
  "Unit Serial",
  "Status",
];

export const scanTableKeys = [
  "batchNumber",
  "cartonSerial",
  "packSerial",
  "unitSerial",
  "status",
];

export function groupItemsByBatch(items: ScannedItem[]): IAddedBatch[] {
  const batchMap = new Map<string, IAddedBatch>();

  for (const item of items) {
    const batchNumber = item.batchNumber.value;
    const inventoryId = item.inventory_id?.value ?? item.id;
    const existing = batchMap.get(batchNumber);

    if (existing) {
      existing.quantity += 1;
      batchMap.set(batchNumber, existing);
    } else {
      batchMap.set(batchNumber, {
        batch_number: batchNumber,
        quantity: 1,
        inventory_id: inventoryId,
        brand_name: item.brandName?.value || "",
        strength: item.strength?.value || "",
        strength_unit: item.strengthUnit?.value || "",
      });
    }
  }

  return Array.from(batchMap.values());
}

export const getLogisticCompaniesDetails = (company: any) => {
  if (!company) return [];
  const result = company?.map((comp: KeyValueObj) => [
    {
      title: "Company Name",
      value: comp?.name ?? "--",
    },
    {
      title: "Address",
      value: `${comp?.street_address || "---"} , ${comp?.local_government_zone || "---"}, ${comp?.city || "---"}, ${comp?.state || "---"}`,
    },
  ]);
  return result;
};

interface IInventoryBatchDetails {
  batch_number: string;
  quantity: number;
  inventory_id: string;
  brand_name: string;
  strength: string;
  strength_unit: string;
}

export const batchTableHeadersPreview = [
  "Batch Number",
  "Quantity",
  "Brand Name",
  "Strength",
  "Strength Unit",
];

export const batchTableKeysPreview = [
  "batch_number",
  "quantity",
  "brand_name",
  "strength",
  "strength_unit",
];

export function getBatchTableDataPreview(data: any) {
  return data?.map((datum: IInventoryBatchDetails) => ({
    batch_number: { type: "string", value: datum.batch_number },
    quantity: { type: "string", value: datum.quantity },
    brand_name: { type: "string", value: datum.brand_name },
    strength: { type: "string", value: datum.strength },
    strength_unit: { type: "string", value: datum.strength_unit },
  }));
}

export const getProductDetails = (formData: any) => {
  const productDetails = formData || {};

  return [
    {
      title: "Category",
      value: productDetails?.category || "--",
    },
    {
      title: "Package Type",
      value: productDetails?.package_type || "--",
    },
    ...(productDetails?.category === "cold_storage"
      ? [
          {
            title: "Min Temperature",
            value: productDetails?.min_temperature || "--",
          },
          {
            title: "Max Temperature",
            value: productDetails?.max_temperature || "--",
          },
        ]
      : []),
    {
      title: "Height",
      value: productDetails?.height || "--",
    },
    {
      title: "Length",
      value: productDetails?.length || "--",
    },
  ];
};
